import { useEffect, useState } from "react";
import { Settings as SettingsIcon, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";


const STRUCTURE_OPTIONS = [
  { id: "seller_carry", label: "Seller Carry-Back Note" },
  { id: "lease_option", label: "Lease with Option to Purchase" },
  { id: "wraparound", label: "Wraparound Mortgage" },
  { id: "subject_to", label: "Subject-To Existing Financing" },
  { id: "installment_sale", label: "Installment Sale / Land Contract" },
];


export default function DealPreferencesPanel() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/documents/preferences");
      setPrefs({
        preferred_down_payment_pct: 10,
        preferred_interest_rate: 6.5,
        preferred_interest_rate_min: 5,
        preferred_interest_rate_max: 8,
        preferred_amortization_years: 30,
        preferred_structures: ["seller_carry"],
        negotiation_assistant_always_visible: false,
        ...r.data,
      });
    } catch {
      toast.error("Failed to load preferences");
    }
  };

  useEffect(() => { load(); }, []);

  const toggleStructure = (id) => {
    const cur = prefs.preferred_structures || [];
    const next = cur.includes(id) ? cur.filter(s => s !== id) : [...cur, id];
    setPrefs({ ...prefs, preferred_structures: next });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/documents/preferences", prefs);
      toast.success("Deal preferences saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!prefs) return null;

  return (
    <div className="ae-card p-5 space-y-4" data-testid="deal-preferences-panel">
      <div className="flex items-center gap-2">
        <Wand2 className="h-4 w-4 text-emerald-400" />
        <h3 className="font-heading text-lg">Deal Preferences</h3>
      </div>
      <p className="text-xs text-slate-500">
        These defaults populate document templates (LOI, Term Sheet, Addendum) and feed the Negotiation Assistant's recommendations.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Down payment %</div>
          <input type="number" step="0.5" min="0" max="100"
                 value={prefs.preferred_down_payment_pct}
                 onChange={(e) => setPrefs({ ...prefs, preferred_down_payment_pct: parseFloat(e.target.value) || 0 })}
                 data-testid="pref-down-pct"
                 className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200 font-mono" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Interest rate %</div>
          <input type="number" step="0.25" min="0" max="20"
                 value={prefs.preferred_interest_rate}
                 onChange={(e) => setPrefs({ ...prefs, preferred_interest_rate: parseFloat(e.target.value) || 0 })}
                 data-testid="pref-interest"
                 className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200 font-mono" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Rate min %</div>
          <input type="number" step="0.25" value={prefs.preferred_interest_rate_min}
                 onChange={(e) => setPrefs({ ...prefs, preferred_interest_rate_min: parseFloat(e.target.value) || 0 })}
                 data-testid="pref-rate-min"
                 className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200 font-mono" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Rate max %</div>
          <input type="number" step="0.25" value={prefs.preferred_interest_rate_max}
                 onChange={(e) => setPrefs({ ...prefs, preferred_interest_rate_max: parseFloat(e.target.value) || 0 })}
                 data-testid="pref-rate-max"
                 className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200 font-mono" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Amortization (yrs)</div>
          <input type="number" min="5" max="40" value={prefs.preferred_amortization_years}
                 onChange={(e) => setPrefs({ ...prefs, preferred_amortization_years: parseInt(e.target.value) || 30 })}
                 data-testid="pref-amort"
                 className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200 font-mono" />
        </label>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Preferred structures</div>
        <div className="flex flex-wrap gap-2" data-testid="pref-structures">
          {STRUCTURE_OPTIONS.map((s) => {
            const on = prefs.preferred_structures?.includes(s.id);
            return (
              <button key={s.id}
                      onClick={() => toggleStructure(s.id)}
                      data-testid={`pref-structure-${s.id}`}
                      className={`px-2.5 py-1 text-xs uppercase tracking-wider rounded-sm border transition-colors ${
                        on ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                           : "bg-white/0 text-slate-400 border-white/10 hover:border-white/20"
                      }`}
              >{s.label}</button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/8">
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
          <input type="checkbox" checked={!!prefs.negotiation_assistant_always_visible}
                 onChange={(e) => setPrefs({ ...prefs, negotiation_assistant_always_visible: e.target.checked })}
                 data-testid="pref-neg-always"
                 className="accent-emerald-500" />
          Show Negotiation Assistant on every opportunity (default: only at Engaged stage)
        </label>
        <button onClick={save} disabled={busy} data-testid="pref-save"
                className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25">
          {busy ? "Saving..." : "Save preferences"}
        </button>
      </div>
    </div>
  );
}
